"use client";
import React, { Fragment, useEffect, useState, useMemo } from "react";
import { useQuery, useMutation } from "@apollo/client";
import Header from "../../components/Header";
import { useParams, useNavigate } from 'react-router-dom';
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import ListGroupMatches from "../../components/tournament/ListGroupMatches";
import FaseChaveColumn from '../../components/tournament/FaseChaveColumn';

import {
  buscarCategoriaTorneioPorId,
  atualizarCategoriaTorneio,
  CategoriaTorneio,
  FaseCategoriaTorneio,
} from '../../services/categoriaTorneioService';
import { PartidaLite as PartidaCategoriaLite } from '../../services/faseGrupoPartidasService';
import { criarPartida } from "../../services/partidaService";
import { PageBreadcrumb } from "../../components/navigation";

type FaseCategoriaTorneioLite = {
  id: string;
  nome: string;
  tipoGrupoChave?: 'GRUPO' | 'CHAVE' | null;
  partidas?: PartidaCategoriaLite[];
  fasesGrupoPartidas?: Array<unknown>;
};

function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
        href: `#simple-tabpanel-${index}`,
        
    };
}

const TournamentCategoryMatches: React.FC = () => {
    const { id } = useParams<{ id: string }>();
  
    const navigate = useNavigate();
    const [category, setCategory] = useState<CategoriaTorneio | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [phases, setPhases] = useState<FaseCategoriaTorneio[]>([]);
    const [modalGrupoOpen, setModalGrupoOpen] = useState<boolean>(false);
    const [tab, setTab] = useState<number>(0);

    const fases = phases ?? [];

    const fasesGrupo = useMemo(
        () => (fases as FaseCategoriaTorneioLite[]).filter(f => f?.tipoGrupoChave === 'GRUPO'),
        [fases]
    );
    const fasesChave = useMemo(
        () => (fases as FaseCategoriaTorneioLite[]).filter(f => f?.tipoGrupoChave === 'CHAVE'),
        [fases]
    );

    const load = async () => {
        if (!id) return;
        setLoading(true);
        setError(null);
        try {
          const data = await buscarCategoriaTorneioPorId(id);
          setCategory(data);
          setPhases(data.fasesCategoria);
        } catch (e: any) {
          setError(e.message || 'Erro ao carregar categoria');
        } finally {
          setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [id]);

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setTab(newValue);
    };

    const handleNovaPartida = async (faseId: string) => {
        if (!id) return;
        await criarPartida({ 
            categoriaTorneioId: id,
            faseCategoriaTorneioId: faseId
        });
        await reloadPartidasFase(faseId);
    };

    async function reloadPartidasFase(faseId: string) {
        try {
            const data = await buscarCategoriaTorneioPorId(id as string);
            setCategory(data);
            setPhases(data.fasesCategoria);
        } catch (e: any) {
            setError(e.message || 'Erro ao recarregar partidas da fase');
        }
    }

    return (
        <Fragment>
            <Header />
            <div className="main-content theme-dark-bg right-chat-active">
                <div className="middle-sidebar-bottom">
                    <div className="middle-sidebar-left">
                        <div className="middle-wrap">
                            <div className="container py-3">
                                <div className="mb-3">
                                    <PageBreadcrumb
                                        items={[
                                            { label: 'Torneios', href: '/admin/tournaments' },
                                            ...(category?.torneio
                                                ? [
                                                    {
                                                        label: category.torneio.nome ?? 'Torneio',
                                                        href: `/admin/edittournament/${category.torneio.id}`,
                                                    },
                                                ]
                                                : []),
                                            {
                                                label: category?.nome ?? 'Categoria',
                                                href: id ? `/admin/editcategorytournament/${id}` : undefined,
                                            },
                                            { label: 'Partidas' },
                                        ]}
                                        onBack={() =>
                                            id ? navigate(`/admin/editcategorytournament/${id}`) : navigate(-1)
                                        }
                                        variant="bootstrap"
                                    />
                                </div>
                            </div>
                            <div className="card w-100 border-0 bg-white shadow-xs p-0 mb-4">
                                <div className="card-header p-4 w-100 bg-current border-0 d-flex rounded-3 align-items-center">
                                    <h4 className="font-xs text-white fw-600 m-0">
                                        Chaveamento da categoria {category?.nome ? `- ${category.nome}` : ''}
                                    </h4>
                                </div>
                                <div className="card-body p-4">
                                    <Tabs value={tab} onChange={handleChange} aria-label="simple tabs example" indicatorColor="primary" textColor="primary">
                                        <Tab  label="Fase de grupos" {...a11yProps(0)} />
                                        <Tab  label="Chaveamento" {...a11yProps(1)} />
                                    </Tabs>
                                    <div role="tabpanel" hidden={tab !== 0} id="simple-tabpanel-0" aria-labelledby="simple-tab-0" className="mt-4">
                                        {tab === 0 && (
                                            fasesGrupo.length === 0 ? (
                                                <div data-testid="grupos-vazio">Nenhuma fase do tipo GRUPO encontrada.</div>
                                            ) : (
                                                fasesGrupo.map(fase => (
                                                    <div key={fase.id} data-testid={`fase-grupo-${fase.id}`} style={{ marginBottom: 24 }}>
                                                        <ListGroupMatches 
                                                            faseCategoriaTorneio={fase as any}  
                                                            categoriaTorneioId={category?.id ?? ''} 
                                                            localTorneio={category?.torneio?.local || {} as any}
                                                            categoriaTorneio={category ? {
                                                              gamesPorSets: category.gamesPorSets ?? null,
                                                              tiebreaks: category.tiebreaks ?? null,
                                                              numeroSets: category.numeroSets ?? null,
                                                            } : null}
                                                        />
                                                    </div>
                                                ))
                                            )
                                        )}
                                    </div>
                                    <div role="tabpanel" hidden={tab !== 1} id="simple-tabpanel-1" aria-labelledby="simple-tab-1" className="mt-4">
                                        {tab === 1 && (
                                            fasesChave.length === 0 ? (
                                                <div data-testid="chave-vazio">Nenhuma fase do tipo CHAVE encontrada.</div>
                                            ) : (
                                                <div
                                                    data-testid="lista-chaves"
                                                    className="d-flex flex-row flex-nowrap overflow-auto gap-3"
                                                >
                                                    {fasesChave.map(fase => (
                                                        <div
                                                            key={fase.id}
                                                            data-testid={`coluna-fase-chave-${fase.id}`}
                                                            className="flex-shrink-0"
                                                        >
                                                            <FaseChaveColumn
                                                                fase={fase as any}
                                                                categoriaTorneioId={category?.id ?? ''}
                                                                localTorneio={category?.torneio?.local || {} as any}
                                                                faseId={fase.id}
                                                                categoriaTorneio={category ? {
                                                                  gamesPorSets: category.gamesPorSets ?? null,
                                                                  tiebreaks: category.tiebreaks ?? null,
                                                                  numeroSets: category.numeroSets ?? null,
                                                                } : null}
                                                                onNovaPartida={handleNovaPartida}
                                                                onSaved={load}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            )
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );

};



export default TournamentCategoryMatches;


